import { ORPCError } from '@orpc/client';
import { useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { rpcClient } from 'src/api/rpc/client';
import { invalidateTaskQueries } from 'src/api/rpc/hooks/tasks/invalidateTaskQueries';
import { toast } from 'src/components/ui/toast';

export type CorrectionCommitResult = 'saved' | 'conflict' | 'failed';

function isConflict(error: unknown): boolean {
  return error instanceof ORPCError && error.code === 'CONFLICT';
}

/**
 * Replace the task correction with the full next working copy.
 * A 409 refetches and leaves the server copy in place.
 */
export function useCommitTaskCorrection(task: { id: string; updatedAt: Date }) {
  const queryClient = useQueryClient();
  const gt = useGT();

  return async (
    correction: Record<string, unknown>,
  ): Promise<CorrectionCommitResult> => {
    try {
      await rpcClient.tasks.updateCorrection({
        id: task.id,
        expectedUpdatedAt: task.updatedAt,
        correction,
      });
      invalidateTaskQueries(queryClient);
      return 'saved';
    } catch (error) {
      invalidateTaskQueries(queryClient);
      if (isConflict(error)) {
        toast.error(
          gt('This task was updated elsewhere. The latest values are shown.', {
            context:
              'toast when saving a task correction conflicts with a newer update',
          }),
        );
        return 'conflict';
      }
      toast.error(
        gt('Could not save this change.', {
          context: 'toast when saving a task correction fails',
        }),
      );
      return 'failed';
    }
  };
}
